import { SearchUserModalApi } from "../../search/user/modal/search-user-modal-api";
import { UserDisplayHelper, type UserDisplayItem } from "../../user/user-display-utils";
import { WysiwygForm } from "../wysiwyg-form";
import { WysiwygMentionRender } from "./wysiwyg-mention-render";

export interface MentionMatch {
    query: string;
    startIndex: number;
    cursorIndex: number;
}

const NAVIGATION_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);

export abstract class MentionControllerBase {
    protected readonly dropdownEl: HTMLDivElement;
    private items: UserDisplayItem[] = [];
    private activeMatch: MentionMatch | null = null;
    protected activeIndex = -1;
    private loadSeq = 0;

    protected constructor(hostEl: HTMLElement) {
        this.dropdownEl = WysiwygMentionRender.createDropdown(hostEl);
    }

    public abstract bind(): void;
    protected abstract resolveMentionMatch(): MentionMatch | null;
    protected abstract doInsertMention(user: UserDisplayItem, match: MentionMatch): void;
    protected abstract doPositionDropdown(match: MentionMatch): void;
    protected abstract isOwnEditorEl(target: Node): boolean;

    /** Override to update ARIA attributes after active index changes (e.g. Quill). */
    protected onActiveIndexChanged(): void {
        // no-op by default
    }

    public handleInputLike(event?: Event): void {
        if (event instanceof KeyboardEvent && NAVIGATION_KEYS.has(event.key)) {
            return;
        }
        void this.refresh();
    }

    public handleKeyDown(event: KeyboardEvent): void {
        if (!this.isDropdownOpen()) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            this.moveActiveIndex(1);
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            event.stopPropagation();
            this.moveActiveIndex(-1);
            return;
        }

        if (event.key === "Enter") {
            const activeItem = this.getActiveItem();
            if (!activeItem || !this.activeMatch) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.commitMention(activeItem, this.activeMatch);
        }
    }

    public handleDropdownMouseDown(event: MouseEvent): void {
        event.preventDefault();
        const option = WysiwygForm.getMentionUserOption(event);
        if (!(option instanceof HTMLElement)) {
            return;
        }
        const id = String(option.dataset.userId || "").trim();
        if (!id) {
            return;
        }
        const item = this.items.find((entry) => entry.id === id);
        if (!item || !this.activeMatch) {
            return;
        }
        this.commitMention(item, this.activeMatch);
    }

    public handleDocumentMouseDown(event: MouseEvent): void {
        const target = event.target;
        if (!(target instanceof Node)) {
            this.hide();
            return;
        }
        if (this.dropdownEl.contains(target) || this.isOwnEditorEl(target)) {
            return;
        }
        this.hide();
    }

    protected async refresh(): Promise<void> {
        const match = this.resolveMentionMatch();
        if (!match) {
            this.hide();
            return;
        }

        this.activeMatch = match;
        const seq = ++this.loadSeq;
        WysiwygMentionRender.renderLoading(this.dropdownEl);
        this.doPositionDropdown(match);

        try {
            const result = await SearchUserModalApi.searchUsers(match.query);
            if (seq !== this.loadSeq) {
                return;
            }
            const data = Array.isArray(result.data) ? result.data : [];
            this.items = (await Promise.all(data.map((entry) => UserDisplayHelper.normalizeUserItem(entry))))
                .filter((item): item is UserDisplayItem => item !== null)
                .filter((item) => this.matchesMentionPrefix(item, match.query));
            this.activeIndex = this.items.length > 0 ? 0 : -1;
            WysiwygMentionRender.renderItems(this.dropdownEl, this.items, this.activeIndex);
            this.onActiveIndexChanged();
        } catch (_error) {
            if (seq !== this.loadSeq) {
                return;
            }
            this.items = [];
            this.activeIndex = -1;
            WysiwygMentionRender.renderEmpty(this.dropdownEl);
            this.onActiveIndexChanged();
        }
    }

    protected hide(): void {
        this.activeMatch = null;
        this.items = [];
        this.activeIndex = -1;
        WysiwygMentionRender.hide(this.dropdownEl);
        this.onActiveIndexChanged();
    }

    private commitMention(user: UserDisplayItem, match: MentionMatch): void {
        this.doInsertMention(user, match);
        this.hide();
    }

    private isDropdownOpen(): boolean {
        return !this.dropdownEl.hidden;
    }

    private moveActiveIndex(step: number): void {
        if (this.items.length === 0) {
            return;
        }
        const lastIndex = this.items.length - 1;
        if (this.activeIndex < 0) {
            this.activeIndex = step > 0 ? 0 : lastIndex;
        } else {
            this.activeIndex += step;
            if (this.activeIndex < 0) {
                this.activeIndex = lastIndex;
            }
            if (this.activeIndex > lastIndex) {
                this.activeIndex = 0;
            }
        }
        WysiwygMentionRender.renderItems(this.dropdownEl, this.items, this.activeIndex);
        this.onActiveIndexChanged();
    }

    private getActiveItem(): UserDisplayItem | null {
        if (this.activeIndex < 0 || this.activeIndex >= this.items.length) {
            return null;
        }
        return this.items[this.activeIndex] ?? null;
    }

    private matchesMentionPrefix(item: UserDisplayItem, query: string): boolean {
        const normalizedQuery = String(query || "")
            .trim()
            .toLowerCase();
        if (normalizedQuery === "") {
            return true;
        }
        const displayName = UserDisplayHelper.ensureAtPrefix(item.displayName).replace(/^@/, "").toLowerCase();
        return displayName.startsWith(normalizedQuery);
    }
}
